import React, { useEffect, useState } from 'react';
import { HiArrowRight, HiStar } from 'react-icons/hi2';
import { fetchScreenshots } from '../utils/supabase';
import Lightbox from './Lightbox';
import './Hero.css';

// Fallback gradient placeholders if no real screenshots are uploaded yet
const FALLBACK_SCREENSHOTS = [
  { id: 'f1', color: '#0071e3', label: 'Fitness' },
  { id: 'f2', color: '#34c759', label: 'Finance' },
  { id: 'f3', color: '#ff9f0a', label: 'Food' },
  { id: 'f4', color: '#af52de', label: 'Social' },
  { id: 'f5', color: '#ff3b30', label: 'Travel' },
  { id: 'f6', color: '#007aff', label: 'Weather' },
  { id: 'f7', color: '#5856d6', label: 'Music' },
  { id: 'f8', color: '#ff2d55', label: 'Dating' },
];

function RealScreenshot({ image, index, onClick }) {
  return (
    <button
      type="button"
      className="hero-screenshot hero-screenshot--real"
      style={{ '--delay': `${index * 0.08}s` }}
      onClick={onClick}
      aria-label="View screenshot"
    >
      <div className="hero-screenshot__device">
        <img
          src={image.url}
          alt={image.label || 'App screenshot'}
          className="hero-screenshot__real-img"
          loading={index < 4 ? 'eager' : 'lazy'}
        />
      </div>
    </button>
  );
}

function FallbackScreenshot({ item, index }) {
  return (
    <div className="hero-screenshot" style={{ '--delay': `${index * 0.1}s` }}>
      <div className="hero-screenshot__device">
        <div className="hero-screenshot__screen" style={{ background: `linear-gradient(145deg, ${item.color}, ${item.color}dd)` }}>
          <div className="hero-screenshot__notch"></div>
          <div className="hero-screenshot__content">
            <div className="hero-screenshot__title-bar">
              <div className="hero-screenshot__dot" style={{ background: 'rgba(255,255,255,0.4)' }}></div>
              <div className="hero-screenshot__line" style={{ width: '60%', background: 'rgba(255,255,255,0.3)' }}></div>
            </div>
            <div className="hero-screenshot__hero-img" style={{ background: 'rgba(255,255,255,0.15)' }}></div>
            <div className="hero-screenshot__lines">
              <div className="hero-screenshot__line" style={{ width: '80%', background: 'rgba(255,255,255,0.25)' }}></div>
              <div className="hero-screenshot__line" style={{ width: '60%', background: 'rgba(255,255,255,0.15)' }}></div>
              <div className="hero-screenshot__line" style={{ width: '70%', background: 'rgba(255,255,255,0.2)' }}></div>
            </div>
            <div className="hero-screenshot__cta-mock" style={{ background: 'rgba(255,255,255,0.3)' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const [screenshots, setScreenshots] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    fetchScreenshots().then(setScreenshots);
  }, []);

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(i => (i - 1 + screenshots.length) % screenshots.length);
  const nextImage = () => setLightboxIndex(i => (i + 1) % screenshots.length);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const hasReal = screenshots.length > 0;

  return (
    <section className="hero">
      <div className="hero__content container">
        <div className="hero__badge">
          <HiStar />
          <span>Up to 67.8% conversion rate</span>
        </div>

        <h1 className="hero__title">
          High-Conversion
          <br />
          <span className="hero__title-accent">App Store Screenshots</span>
        </h1>

        <p className="hero__subtitle">
          That's all we do. We design screenshots that turn browsers into downloads —
          for iOS, iPad & Android. Delivered in 48h.
        </p>

        <div className="hero__actions">
          <button className="btn btn-primary btn-lg" onClick={() => scrollTo('pricing')}>
            See Pricing <HiArrowRight />
          </button>
        </div>

        <div className="hero__stats">
          <div className="hero__stat">
            <strong>67.8%</strong>
            <span>Conversion Rate Achieved</span>
          </div>
          <div className="hero__stat-divider"></div>
          <div className="hero__stat">
            <strong>1,770+</strong>
            <span>Downloads in One Spike</span>
          </div>
          <div className="hero__stat-divider"></div>
          <div className="hero__stat">
            <strong>24h</strong>
            <span>Avg. Delivery</span>
          </div>
        </div>
      </div>

      {/* Auto-scrolling marquee — pauses on hover */}
      <div className="hero__showcase">
        <div className="hero__showcase-row">
          {hasReal
            ? [...screenshots, ...screenshots].map((img, i) => (
                <RealScreenshot
                  key={i}
                  image={img}
                  index={i % screenshots.length}
                  onClick={() => openLightbox(i % screenshots.length)}
                />
              ))
            : [...FALLBACK_SCREENSHOTS, ...FALLBACK_SCREENSHOTS].map((item, i) => (
                <FallbackScreenshot key={i} item={item} index={i % FALLBACK_SCREENSHOTS.length} />
              ))
          }
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={screenshots}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
}
